<template>
  <AdminForm :config="config" :form="form" :isBusy="isBusy" :options="options" @submit:create:success="postFile"
    @submit:update:success="postFile"></AdminForm>
</template>

<script>
import AdminForm from "@/components/Global/AdminForm";
import config, { esmPrice } from "./config";

export default {
  name: "ProductForm",
  components: {
    AdminForm,
  },
  props: ["id_to_edit"],
  data() {
    return {
      config: config,
      form: {
        equipment_group_id: null,
        supplier_id: null,
        calibration_provider_id: null,
        group_slug: null,
        sap_activity: null,
        status_id: this.$store.getters["status/getItem"]("product_on_sale").id,
        other_pictures: [],
        documents: [],
        related_products: [],
        parents: [],
        return_mode: null,
        companies: [],
        product_companies: [],
        request_attribute_ids: [],
        billable_days: [],
      },
      isBusy: false,
      initialForm: {},
    };
  },
  computed: {
    options: function () {
      return {
        parent_product_type_options: this.$store.getters["product/getParentTypesForSelect"],
        child_product_type_options: this.$store.getters["product/getChildTypesForSelect"],
        // Catalogue unifié : toutes les familles sont proposées, quelle que soit la
        // nature du produit (une même famille peut contenir de la location et de la vente).
        equipment_group_id_options: this.$store.getters["equipment_group/getAllForSelect"],
        supplier_id_options: this.$store.getters["company/getByTypeCode"]("FRNI").selectOptions(
          "id",
          "name"
        ),
        calibration_provider_id_options: this.$store.getters["company/getByTypeCode"](
          ["PRST", "FRNI"]
        ).selectOptions("id", "name"),
        group_slug_options: this.$store.getters["renting_preparation/getGroups"],
        status_id_options: this.$store.getters["status/getByGroup"]("product").selectOptions(
          "id",
          "name"
        ),
        id_options: this.$store.getters["product/getByStatusSlug"]("product_on_sale").selectOptions(
          "id",
          "name"
        ),
        return_mode_options: this.$store.getters["product/getReturnModes"],
        tags_options: this.$store.getters["tag/getAll"].map(t => ({text: t.name})),
        // Location pour compte : les attributs proposés dépendent de la famille choisie.
        // Sans ce filtre, la liste offrirait les cent soixante-trois attributs du catalogue
        // — ceux d'un tube à cintrer sur une fiche de nacelle.
        request_attribute_ids_options: this.attributeOptionsForFamily,
        billable_days_options: this.$store.getters["product/getBillableDaysForSelect"],
      };
    },
    /**
     * Attributs proposables pour la famille choisie.
     *
     * Une sous-famille hérite des attributs de sa racine : une nacelle est un matériel de
     * travail en hauteur, et le paramétrage client range à la racine ce qui vaut pour toute
     * la famille (nature du sol, largeur de passage) et à la sous-famille ce qui lui est
     * propre (type d'énergie, déport).
     *
     * Tant qu'aucune famille n'est choisie, la liste reste vide plutôt que complète : proposer
     * cent soixante-trois attributs reviendrait à ne rien proposer.
     */
    attributeOptionsForFamily: function () {
      const familyId = this.form.equipment_group_id;
      if (!familyId) {
        return [];
      }

      const family = this.$store.getters["equipment_group/getItem"](familyId);
      const rootId = family && family.parent_equipment_group_id
        ? family.parent_equipment_group_id
        : familyId;

      return this.$store.getters["request_attribute/getAll"]
        .filter((attribute) => {
          // Un attribut peut relever de plusieurs familles — « Type d'énergie » vaut pour une
          // nacelle comme pour un compacteur : il suffit qu'une seule corresponde.
          const groupIds = attribute.equipment_group_ids || [];
          if (!attribute.active || !groupIds.length) {
            return false;
          }
          return groupIds.some((groupId) => groupId == familyId || groupId == rootId);
        })
        .map((attribute) => ({
          value: attribute.id,
          text: this.describeAttribute(attribute),
        }));
    },
  },
  watch: {
    /**
     * Changer de famille change les attributs proposés : ceux qui ne sont plus offerts
     * doivent tomber, sans quoi la fiche garderait des attributs d'une autre famille,
     * invisibles dans le champ mais bien enregistrés.
     */
    "form.equipment_group_id": function () {
      if (!Array.isArray(this.form.request_attribute_ids)) {
        return;
      }
      // La bibliothèque arrive de façon asynchrone. Tant qu'elle est vide, on ne peut pas
      // savoir ce qui est proposable — et élaguer sur une liste vide effacerait les
      // attributs enregistrés d'une fiche qu'on vient d'ouvrir.
      if (!this.$store.getters["request_attribute/getAll"].length) {
        return;
      }
      const allowed = this.attributeOptionsForFamily.map((option) => option.value);
      this.form.request_attribute_ids = this.form.request_attribute_ids.filter((id) =>
        allowed.some((value) => value == id)
      );
    },
    "form.parents": {
      handler(parents) {
        if (parents.filter(parent => parent.child_product_type == 'product_variation').length > 1) {
          setTimeout(() => {
            parents.findLast(parent => parent.child_product_type == 'product_variation').child_product_type = null
          }, 100);
          this.$toastr.warning("Un produit ne peut pas être une déclinaison de plusieurs produits parents")
        }
        if (parents.filter(parent => parent.child_product_type == 'product_variation').length >= 1 && parents.filter(parent => parent.child_product_type == 'product_in_lot').length >= 1) {
          setTimeout(() => {
            parents.find(parent => parent.child_product_type == 'product_in_lot').child_product_type = null
          }, 100);
          this.$toastr.warning("Une déclinaison ne peut pas être mise dans un lot de produits")
        }
      },
      deep: true,
    }
  },
  mounted() {
    // Duplication
    // Chargement de la copie qui a été préparée s'il y en a une
    const copy = this.$store.getters[config.storeNamespace + "/getCopy"];
    if (!this.id_to_edit && copy && copy.name) {
      this.form = {
        ...copy,
        tags: copy.tags ? copy.tags.map(t => ({text: t})) : []
      };
      this.$store.commit("product/emptyCopy");
    }
    // Edition
    const id =
      this.$route.name == config.editRouteName && this.$route.params.id
        ? this.$route.params.id
        : this.id_to_edit || null;

    if (id) {
      this.isBusy = true;
      this.$store
        .dispatch(config.storeNamespace + "/getItem", {
          id: id,
          wait: true,
        })
        .then(() => {
          let item = _.cloneDeep(this.$store.getters[config.storeNamespace + "/getItem"](id));
          this.form = {
            ...item,
            equipment_group_id: item.equipment_group ? item.equipment_group.id : null,
            supplier_id: item.supplier ? item.supplier.id : null,
            calibration_provider_id: item.calibration_provider
              ? item.calibration_provider.id
              : null,
            status_id: item.status ? item.status.id : null,
            picture_id: item.picture ? item.picture.id : null,
            picture: item.picture ? item.picture.file : null,
            other_pictures: item.documents
              ? item.documents.filter((document) => document.type_slug == "product-other-picture")
              : [],
            technical_sheet_id: item.technical_sheet ? item.technical_sheet.id : null,
            technical_sheet: item.technical_sheet ? item.technical_sheet.file : null,
            documents: item.documents
              ? item.documents.filter((document) => document.type_slug == "product-document")
              : [],
            related_products: item.related_products ? item.related_products : [],
            product_companies: item.product_companies ? item.product_companies : [],
            sale_price_1: esmPrice(item, "range1"),
            sale_price_2: esmPrice(item, "range2"),
            sale_price_3: esmPrice(item, "range3"),
            sale_price_4: esmPrice(item, "range4"),
            sale_price_5: esmPrice(item, "range5"),
            stock_mini: item.stock_mini ? item.stock_mini : "0",
            parents: item.parent_product ? item.parent_product.map(item => ({
              ...item,
              product_parent_id: item.product_parent.id
            })) : [],
            name : item.parent_product && item.parent_product.filter(parent => parent.child_product_type === 'product_variation').length && item.parent_product_type != 'product_with_variations' ? item.name.replace(item.parent_product.find(parent => parent.child_product_type === 'product_variation').product_parent.name+' - ', '') : item.name,
            tags: item.tags ? item.tags.map(t => ({text: t})) : [],
            // Location pour compte : l'API renvoie les jours facturables en chaîne
            // (« 1,2,3,4,5,6 ») et NULL pour « tous les jours ». La multi-sélection
            // travaille sur des nombres.
            billable_days: item.billable_days
              ? String(item.billable_days).split(",").map(Number)
              : [],
            request_attribute_ids: item.request_attribute_ids ? item.request_attribute_ids : [],
          };
          // On sauvegarde les données initiales, donc avant modification dans le form, pour ensuite
          // les utiliser dans la gestion des fichiers joints
          this.initialForm = _.cloneDeep(this.form);
          this.isBusy = false;
        });
    }

    this.$store.dispatch("equipment_group/getAll");
    this.$store.dispatch("company/getAllForSelect");
    // Sans écraser la fiche ouverte derrière la modale : elle lit son produit complet dans le
    // state, dont les prix disparaîtraient au profit de la version simplifiée de la liste.
    this.$store.dispatch("product/getAllForSelect", { merge: true });
    this.$store.dispatch("tag/getAll")
    // Bibliothèque d'attributs de demande : elle alimente la multi-sélection de la fiche,
    // qui n'apparaît que sur un produit de nature « Location pour compte ».
    this.$store.dispatch("request_attribute/getAll")
  },
  methods: {
    /**
     * Libellé d'un attribut dans la liste de sélection : ce qu'il demande, et ce qu'il
     * proposera au demandeur.
     *
     * Le libellé seul ne dit rien de ce que le champ attend. « Type d'énergie » et « Type
     * d'utilisation » se ressemblent ; leurs réponses non. Les valeurs sont donc affichées à
     * côté, et pour un champ numérique c'est l'unité qui les remplace — c'est elle qui dit ce
     * que le demandeur devra saisir.
     */
    describeAttribute(attribute) {
      const label = attribute.label;
      const choices = attribute.choices
        ? String(attribute.choices).split("\n").filter((v) => v.trim()).join(" / ")
        : null;

      if (attribute.field_type === "number" || attribute.field_type === "boolean_number") {
        const type = this.$store.getters["request_attribute/getFieldTypeLabel"](attribute.field_type);
        return label + " — " + (attribute.unit ? type + " (" + attribute.unit + ")" : type);
      }
      if (attribute.field_type === "boolean") {
        return label + " — Oui / Non";
      }
      if (choices) {
        // Une liste très longue rend la ligne illisible : on la coupe en annonçant le reste.
        const values = choices.length > 70 ? choices.slice(0, 70).trim() + "…" : choices;
        return label + " — " + values;
      }
      return label + " — " + this.$store.getters["request_attribute/getFieldTypeLabel"](attribute.field_type);
    },
    postFile(response) {
      const product_id = response.data.object.id;

      // Gestion de la photo principale supprimée
      // On compare les données du formulaire avant modification avec mes données modifiées
      // pour identifier les fichiers supprimés
      if (this.initialForm.picture && this.initialForm.picture_id) {
        // Si le fichiers courant n'est plus dans les fichiers du formulaire, c'est qu'il faut le supprimer
        if (this.form.picture != this.initialForm.picture) {
          this.$store
            .dispatch("stored_file/deleteItem", this.initialForm.picture_id)
            .then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
        }
      }

      // Si il ya une photo principale
      if (this.form.picture && typeof this.form.picture == "object" && this.form.picture.type) {
        // Si il ne s'agit pas d'un fichier déjà présent
        let item = {
          product_id: product_id,
          type_slug: "product-picture",
          format: this.form.picture.type,
          file: this.form.picture,
        };

        this.$store.dispatch("stored_file/addItem", item).then((response) => {
          this.$store.dispatch(config.storeNamespace + "/getItem", {
            id: product_id,
          });
        });
      }

      // Gestion des photos supprimés
      // On compare les données du formulaire avant modification avec mes données modifiées
      // pour identifier les fichiers supprimés
      if (this.initialForm.other_pictures) {
        this.initialForm.other_pictures.forEach((fileLine) => {
          // Si le fichiers courant n'est plus dans les fichiers du formulaire, c'est qu'il faut le supprimer
          if (!this.form.other_pictures.find((item) => item.id == fileLine.id)) {
            this.$store.dispatch("stored_file/deleteItem", fileLine.id).then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
          }
        });
      }

      // Si il ya des photos
      if (this.form.other_pictures) {
        this.form.other_pictures.forEach((fileLine) => {
          // Si il ne s'agit pas d'un fichier déjà présent
          if (!fileLine.id) {
            let item = {
              product_id: product_id,
              type_slug: "product-other-picture",
              format: fileLine.file.type,
              file: fileLine.file,
            };

            this.$store.dispatch("stored_file/addItem", item).then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
          }
        });
      }

      // Gestion de la notice supprimée
      // On compare les données du formulaire avant modification avec mes données modifiées
      // pour identifier les fichiers supprimés
      if (this.initialForm.technical_sheet && this.initialForm.technical_sheet_id) {
        // Si le fichiers courant n'est plus dans les fichiers du formulaire, c'est qu'il faut le supprimer
        if (this.form.technical_sheet != this.initialForm.technical_sheet) {
          this.$store
            .dispatch("stored_file/deleteItem", this.initialForm.technical_sheet_id)
            .then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
        }
      }

      // Si il ya une notice
      if (
        this.form.technical_sheet &&
        typeof this.form.technical_sheet == "object" &&
        this.form.technical_sheet.type
      ) {
        // Si il ne s'agit pas d'un fichier déjà présent
        let item = {
          product_id: product_id,
          type_slug: "product-technical-sheet",
          format: this.form.technical_sheet.type,
          file: this.form.technical_sheet,
        };

        this.$store.dispatch("stored_file/addItem", item).then((response) => {
          this.$store.dispatch(config.storeNamespace + "/getItem", {
            id: product_id,
          });
        });
      }

      // Gestion des documents téléchargeables supprimés
      // On compare les données du formulaire avant modification avec mes données modifiées
      // pour identifier les fichiers supprimés
      if (this.initialForm.documents) {
        this.initialForm.documents.forEach((fileLine) => {
          // Si le fichiers courant n'est plus dans les fichiers du formulaire, c'est qu'il faut le supprimer
          if (!this.form.documents.find((item) => item.id == fileLine.id)) {
            this.$store.dispatch("stored_file/deleteItem", fileLine.id).then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
          }
        });
      }

      // Si il ya des documents téléchargeables
      if (this.form.documents) {
        this.form.documents.forEach((fileLine) => {
          // Si il ne s'agit pas d'un fichier déjà présent
          if (!fileLine.id) {
            let item = {
              product_id: product_id,
              type_slug: "product-document",
              format: fileLine.file.type,
              file: fileLine.file,
            };

            this.$store.dispatch("stored_file/addItem", item).then((response) => {
              this.$store.dispatch(config.storeNamespace + "/getItem", {
                id: product_id,
              });
            });
          }
        });
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Product/ProductForm.vue